import React, { useEffect, useRef, useState } from 'react';
import { getDevicePlatform } from './utility';
import { loadEpigraphAR } from '../utils/epigraphLoader';

interface SharedEpigraphViewerProps {
    sku?: string;
    gaMeasurementId?: string;
    foregroundImageUrl?: string;
    mobileContainerClassName?: string;
    pictureClassName?: string;
}

type EpigraphTourElement = HTMLElement & {
    updateComplete?: Promise<boolean>;
    showFullScreenModal?: () => void;
};

const SharedEpigraphViewer: React.FC<SharedEpigraphViewerProps> = ({
    sku,
    gaMeasurementId,
    foregroundImageUrl,
    mobileContainerClassName,
    pictureClassName,
}) => {
    const tourRef = useRef<EpigraphTourElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [loadRequested, setLoadRequested] = useState(false);
    const [epigraphReady, setEpigraphReady] = useState(false);
    const [error, setError] = useState(false);
    // Resolve after hydration so the server and initial client markup agree.
    const [isMobile, setIsMobile] = useState<boolean | null>(null);
    const [tourRequested, setTourRequested] = useState(false);

    useEffect(() => {
        const query = window.matchMedia('(max-width: 767px)');
        const update = () =>
            setIsMobile(query.matches || Boolean(getDevicePlatform.any()));
        update();
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (isMobile !== false || loadRequested || !sku) return;
        if (typeof IntersectionObserver === 'undefined') {
            setLoadRequested(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setLoadRequested(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px 0px' }
        );
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [isMobile, loadRequested, sku]);

    useEffect(() => {
        if (!loadRequested || !sku) return;
        let active = true;
        loadEpigraphAR()
            .then(() => {
                if (active) setEpigraphReady(true);
            })
            .catch(() => {
                if (active) setError(true);
            });
        return () => {
            active = false;
        };
    }, [loadRequested, sku]);

    useEffect(() => {
        let active = true;
        const tour = tourRef.current;
        if (isMobile && epigraphReady && tourRequested) {
            // Epigraph's first render hides modal viewers. Open after that render.
            void Promise.resolve(tour?.updateComplete).then(() => {
                if (active) tour?.showFullScreenModal?.();
            });
        }
        return () => {
            active = false;
        };
    }, [isMobile, epigraphReady, tourRequested]);

    if (error) {
        return (
            <div className={mobileContainerClassName} role="status">
                Unable to load the 3D tour. Please reload the page to try again.
            </div>
        );
    }

    const tour = epigraphReady && isMobile !== null && (
        <epigraph-tour
            ref={tourRef}
            sku={sku}
            ga-measurement-id={gaMeasurementId}
            splash-screen-label="Loading 3D tour..."
            splash-screen-text="Loading 3D tour..."
            {...(isMobile ? { 'modal-view': true } : {})}
            style={
                isMobile
                    ? {
                          width: '100vw',
                          height: '100dvh',
                          background: '#fff',
                          overflow: 'clip',
                      }
                    : undefined
            }
        />
    );

    if (isMobile || isMobile === null) {
        return (
            <div className={mobileContainerClassName}>
                <button
                    type="button"
                    aria-label="Open 3D sauna tour"
                    aria-busy={tourRequested && !epigraphReady}
                    disabled={isMobile === null}
                    onClick={() => {
                        setLoadRequested(true);
                        setTourRequested(true);
                        tourRef.current?.showFullScreenModal?.();
                    }}
                    style={{ width: '100%' }}
                >
                    {foregroundImageUrl ? (
                        <picture className={pictureClassName}>
                            <img
                                src={foregroundImageUrl}
                                alt="View the 3D tour"
                                loading="lazy"
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </picture>
                    ) : (
                        'View the 3D tour'
                    )}
                </button>
                {tourRequested && !epigraphReady && (
                    <div role="status">Loading 3D tour...</div>
                )}
                {tourRequested && tour}
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ minHeight: 780 }}>
            {tour || (
                <>
                    <button
                        type="button"
                        onClick={() => setLoadRequested(true)}
                        style={{ width: '100%', height: 780 }}
                    >
                        {foregroundImageUrl ? (
                            <img
                                src={foregroundImageUrl}
                                alt="View the 3D tour"
                                loading="lazy"
                                style={{
                                    width: '100%',
                                    maxHeight: 780,
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            'View the 3D tour'
                        )}
                    </button>
                    {loadRequested && (
                        <div role="status">Loading 3D tour...</div>
                    )}
                </>
            )}
        </div>
    );
};

export default SharedEpigraphViewer;
